import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Result, Space, Tag, Typography } from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import { rbacApi } from "../api/bim/bimService";

/**
 * Project-level RBAC on the client. The backend is the authority (every refused call answers 403 with
 * code PERMISSION_DENIED / NO_PROJECT_ACCESS); this module only explains the refusal and lets the user ask
 * the organisation admins for access.
 */
export interface AccessDenial {
  code?: string; message?: string; permission?: string; action?: string;
  projectId?: number | null; projectName?: string; suggestedRoles?: string[];
}
const EVT = "px-access-denied";
const DENIAL_CODES = new Set(["PERMISSION_DENIED", "NO_PROJECT_ACCESS", "NOT_IN_ORGANISATION"]);

/** called by the API clients for every 403 that carries a denial body */
export function reportDenial(data: any): boolean {
  const d = data?.code ? data : data?.response?.code ? data.response : data?.message?.code ? data.message : null;
  if (!d || !DENIAL_CODES.has(d.code)) return false;
  // reading data the role may not see (e.g. a tab loading the BOQ): the screen shows the message inline;
  // the dialog is for actions the user started
  if (String(d.permission || "").endsWith(".view")) return false;
  window.dispatchEvent(new CustomEvent(EVT, { detail: d }));
  return true;
}
export const showAccessDenied = (d: AccessDenial) => window.dispatchEvent(new CustomEvent(EVT, { detail: { code: "PERMISSION_DENIED", ...d } }));

// ------------------------------------------------------------------ project access context
export interface ProjectAccess {
  loading: boolean; projectId: number | null; projectName?: string;
  isOrgAdmin: boolean; isMember: boolean; roles: { id: number; name: string }[]; permissions: string[];
  labels: Record<string, string>;
  can: (perm: string) => boolean;
  refresh: () => void;
}
const ALLOW_ALL: ProjectAccess = { loading: false, projectId: null, isOrgAdmin: false, isMember: true, roles: [], permissions: [], labels: {}, can: () => true, refresh: () => undefined };
const Ctx = createContext<ProjectAccess>(ALLOW_ALL);

/** loads the signed-in user's permissions on the project once (and on refresh / tab focus) */
export const ProjectAccessProvider: React.FC<{ projectId: number | null; projectName?: string; children: React.ReactNode }> = ({ projectId, projectName, children }) => {
  const [state, setState] = useState<Omit<ProjectAccess, "can" | "refresh">>({ ...ALLOW_ALL, loading: true, projectId, projectName });
  const load = useCallback(() => {
    if (!projectId) return;
    rbacApi.me(projectId).then((r: any) => {
      const d = r?.data ?? {}; const p = d.project || {};
      setState({ loading: false, projectId, projectName: p.projectName || projectName, isOrgAdmin: !!p.isOrgAdmin, isMember: !!p.isMember,
        roles: p.roles || [], permissions: p.permissions || [], labels: d.labels || {} });
    }).catch(() => setState((s) => ({ ...s, loading: false })));   // the backend still enforces; never block the UI on a failed lookup
  }, [projectId, projectName]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const f = () => load(); window.addEventListener("focus", f); return () => window.removeEventListener("focus", f); }, [load]);
  const value = useMemo<ProjectAccess>(() => ({
    ...state, refresh: load,
    can: (perm: string) => state.loading || state.isOrgAdmin || state.permissions.includes(perm),
  }), [state, load]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
export const useProjectAccess = () => useContext(Ctx);

/**
 * Wraps one action control. Allowed: the control works as usual. Not allowed: it stays visible (with a lock
 * hint) and a click explains the refusal and offers "Request access" instead of running the action.
 */
export const PermGate: React.FC<{ perm: string; action?: string; block?: boolean; children: React.ReactNode }> = ({ perm, action, block, children }) => {
  const a = useProjectAccess();
  if (a.can(perm)) return <>{children}</>;
  const label = action || a.labels[perm] || perm;
  // capture phase: the click never reaches the button / Popconfirm / Upload underneath
  const deny = (e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); showAccessDenied({ permission: perm, action: label, projectId: a.projectId, projectName: a.projectName }); };
  return (
    <span data-access-locked="true" title={`You don't have access to ${label}`} onClickCapture={deny} onDropCapture={deny}
      style={block ? { display: "block", position: "relative", opacity: 0.6, cursor: "not-allowed" } : { display: "inline-flex", alignItems: "center", gap: 4, opacity: 0.6, cursor: "not-allowed" }}>
      <LockOutlined style={block ? { position: "absolute", top: 8, right: 10, zIndex: 1, color: "#9ca3af" } : { fontSize: 12, color: "#9ca3af" }} />
      {/* the control itself never gets the pointer (a disabled button would swallow the click): the wrapper answers it */}
      <span style={{ pointerEvents: "none", display: block ? "block" : "inline-flex" }}>{children}</span>
    </span>
  );
};

// ------------------------------------------------------------------ the dialog (mounted once in the layout)
export const AccessDeniedHost: React.FC = () => {
  const [d, setD] = useState<AccessDenial | null>(null);
  const [state, setState] = useState<"idle" | "sending" | "sent" | "already" | "error">("idle");
  const [info, setInfo] = useState<string>("");
  useEffect(() => {
    const h = (e: any) => { setD(e.detail); setState("idle"); setInfo(""); };
    window.addEventListener(EVT, h); return () => window.removeEventListener(EVT, h);
  }, []);
  if (!d) return null;
  const what = d.action || d.permission || "this action";
  const request = async () => {
    if (!d.permission) return;
    setState("sending");
    try {
      const r: any = (await rbacApi.requestAccess({ projectId: d.projectId || undefined, permission: d.permission, action: d.action }))?.data ?? {};
      if (r.sent) { setState("sent"); setInfo(r.suggestedRoles?.length ? `Suggested role: ${r.suggestedRoles.join(" or ")}.` : ""); }
      else if (r.reason === "already-requested") setState("already");
      else { setState("error"); setInfo(r.reason === "no-administrator" ? "No administrator was found for this organisation." : r.error || "The request could not be sent."); }
    } catch (e: any) { setState("error"); setInfo(e?.response?.data?.message || "The request could not be sent."); }
  };
  return (
    <Modal open onCancel={() => setD(null)} footer={null} width={480} title={<Space><LockOutlined style={{ color: "#d97706" }} />Access required</Space>} destroyOnClose>
      <Typography.Paragraph style={{ marginBottom: 12 }}>
        {d.code === "NO_PROJECT_ACCESS"
          ? <>You don't have access to <strong>{d.projectName || "this project"}</strong>.</>
          : <>You don't have permission to <strong>{what}</strong>{d.projectName ? <> on <strong>{d.projectName}</strong></> : null}.</>}
        {" "}Contact your administrator for access.
      </Typography.Paragraph>
      {d.suggestedRoles?.length ? <div style={{ marginBottom: 12, fontSize: 13, color: "#6b7280" }}>Roles that allow this: {d.suggestedRoles.map((r) => <Tag key={r}>{r}</Tag>)}</div> : null}
      {state === "sent" && <Alert type="success" showIcon message="Request sent to your administrator" description={`You will be able to ${what} once they give you the role. ${info}`} style={{ marginBottom: 12 }} />}
      {state === "already" && <Alert type="info" showIcon message="You already requested this access" description="Your administrator has been notified within the last hour." style={{ marginBottom: 12 }} />}
      {state === "error" && <Alert type="error" showIcon message={info} style={{ marginBottom: 12 }} />}
      <Space style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={() => setD(null)}>Close</Button>
        {d.permission && state !== "sent" && state !== "already" && (
          <Button type="primary" icon={<MailOutlined />} loading={state === "sending"} onClick={request}>Request access</Button>
        )}
      </Space>
    </Modal>
  );
};

/** full-page state for a project the user may not open */
export const NoProjectAccess: React.FC<{ projectId?: number | null }> = ({ projectId }) => (
  <Result status="403" title="You don't have access to this project"
    subTitle="Contact your administrator to be added to the project."
    extra={<Button type="primary" icon={<MailOutlined />} onClick={() => showAccessDenied({ code: "NO_PROJECT_ACCESS", permission: "model.view", action: "open this project", projectId: projectId ?? null })}>Request access</Button>} />
);

/** a project area the role may not even view: explains it inside the tab (no data is requested) */
export const ViewGate: React.FC<{ perm: string; what: string; children: React.ReactNode }> = ({ perm, what, children }) => {
  const a = useProjectAccess();
  if (a.can(perm)) return <>{children}</>;
  return (
    <Result status="403" title={`You don't have access to view ${what}`}
      subTitle={a.roles.length ? `Your role on this project (${a.roles.map((r) => r.name).join(", ")}) does not include it. Contact your administrator for access.` : "Contact your administrator for access."}
      extra={<Button type="primary" icon={<MailOutlined />} onClick={() => showAccessDenied({ permission: perm, action: `view ${what}`, projectId: a.projectId, projectName: a.projectName })}>Request access</Button>} />
  );
};
